import { useEffect, useMemo, useRef, useState } from "react";
import { useRoute, useLocation, useSearch } from "wouter";
import {
  useCreateAccountMembershipBatch,
  useGetAccount,
  useListInventoryPlans,
  useListSundayAccessRates,
  useListSpaceResources,
  useListAccountContacts,
  useListSpaces,
  useListAccountMemberships,
  getListAccountMembershipsQueryKey,
  getListAccountProformasQueryKey,
  getListSpaceResourcesQueryKey,
  getGetAccountQueryKey,
  getListInventoryPlanPeriodsQueryOptions,
  getListInventoryPlanPeriodsQueryKey,
  type SpaceResource,
  type InventoryPlan,
  type InventoryPlanPeriod,
  type Membership,
} from "@workspace/api-client-react";
import {
  billingDayOf,
  commonAccountAnchor,
  nextBillingDayOnStartChange,
} from "@/lib/billing-anchor";
import {
  computeQuotePricing,
  type QuoteLineInput,
  type PaymentMode,
} from "@workspace/billing";
import { useQueryClient, useQueries } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import {
  ArrowLeft,
  Loader2,
  X,
  Check,
  Monitor,
  Building2,
  Users,
  Presentation,
  CalendarDays,
} from "lucide-react";

/** Space-type icon by resource type (calendar fallback for passes/events). */
function PlanIcon({
  resourceType,
  className,
}: {
  resourceType?: string | null;
  className?: string;
}) {
  const cls = className ?? "h-5 w-5";
  switch (resourceType) {
    case "Workstation":
      return <Monitor className={cls} />;
    case "Private_Cabin":
      return <Building2 className={cls} />;
    case "Meeting_Room":
      return <Users className={cls} />;
    case "Conference_Room":
      return <Presentation className={cls} />;
    default:
      return <CalendarDays className={cls} />;
  }
}

// 0068 — Multi-line "Add Plan" deal builder, full-page route
// (/accounts/:id/add-plan). The plan's configured discount is applied
// automatically (not an editable column); each line is just Plan · Qty ·
// Months and shows Monthly + Total. Live pricing via the shared
// computeQuotePricing helper; save → memberships + proforma ESTIMATE.


const RESOURCE_TYPE_LABELS: Record<string, string> = {
  Workstation: "Workstation",
  Private_Cabin: "Private Cabin",
  Meeting_Room: "Meeting Room",
  Conference_Room: "Conference Room",
};

function formatINR(n: number): string {
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 2 }).format(n)}`;
}

/** Human label for a plan's catalog billing cycle (inventory_plans.billing_cycle). */
const BILLING_CYCLE_LABELS: Record<string, string> = {
  daily: "Daily",
  monthly: "Monthly",
  quarterly: "Quarterly",
  semiannual: "Half-yearly",
  annual: "Yearly",
};

/** Add N whole months to a YYYY-MM-DD date (day clamped to month length). */
function addMonths(isoDate: string, months: number): string {
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return isoDate;
  const base = new Date(Date.UTC(y, m - 1 + months, 1));
  const dim = new Date(Date.UTC(base.getUTCFullYear(), base.getUTCMonth() + 1, 0)).getUTCDate();
  base.setUTCDate(Math.min(d, dim));
  return base.toISOString().slice(0, 10);
}

/**
 * Committed term in whole calendar months for an INCLUSIVE start→end window.
 * The end date is the last day of the term, so a period from the 1st to a
 * month's last day counts that whole month: Jul 1 → Dec 31 = 6 (not 5),
 * Jun 1 → Dec 31 = 7, Jun 1 → Jan 31 = 8. Always >= 1. Drives the period
 * discount slab + the displayed duration.
 */
function monthsBetween(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 1;
  const [sy, sm, sd] = startIso.split("-").map(Number);
  const [ey, em, ed] = endIso.split("-").map(Number);
  if (!sy || !ey) return 1;
  let months = (ey - sy) * 12 + (em - sm);
  // End day reaching/​passing the start day means the final month is (fully)
  // consumed — count it. (Jul 1 → Dec 31: 5 + 1 = 6.)
  if (ed >= sd) months += 1;
  return Math.max(1, months);
}

/** Days from the start date up to the next billing-day anchor (0 if aligned). */
function stubDaysOf(startDate: string, billingDay: number): number {
  const startDay = Number(startDate.slice(8, 10));
  const [y, m] = startDate.split("-").map(Number);
  const dim = y && m ? new Date(y, m, 0).getDate() : 30;
  // 1..31; a day the start month lacks collapses to its last day (server does
  // the same in anchorDayInMonth), so a 31st anchor in a 30-day month is aligned.
  const wanted = Math.min(31, Math.max(1, billingDay || 1));
  const anchor = Math.min(wanted, dim);
  if (!startDay || startDay === anchor) return 0;
  if (anchor > startDay) return anchor - startDay;
  const [ny, nm] = m === 12 ? [y + 1, 1] : [y, m + 1];
  const nextAnchor = Math.min(wanted, new Date(ny, nm, 0).getDate());
  return dim - startDay + nextAnchor;
}

/**
 * Membership-eligible plans only. Add Plan = Recurring memberships only.
 * Phase 29: ALL Non_Recurring plans (Day/Week passes, Event, Shooting,
 * Meeting/Conference rooms) are sold as BOOKINGS via New Booking
 * (→ /pass-sale or /booking), never as memberships here.
 */
function isMembershipPlan(p: InventoryPlan): boolean {
  // Phase 29: ALL Non_Recurring plans (Day/Week passes, Event, Shooting, Meeting/Conference rooms)
  // are sold as BOOKINGS via New Booking (→ /pass-sale or /booking), never as memberships here.
  // Add Plan = recurring memberships only.
  if (p.cadence === "Non_Recurring") return false;
  return true;
}

type CycleMode = "Monthly" | "Quarterly" | "Six_Month" | "Yearly";
type BillingMode = "Recurring" | "One_Time";

/** Months in one billing cycle — the committed term for an open-ended recurring
 *  line is one cycle ("first full cycle bills now"). */
const CYCLE_MONTHS: Record<CycleMode, number> = {
  Monthly: 1,
  Quarterly: 3,
  Six_Month: 6,
  Yearly: 12,
};
const CYCLE_LABEL: Record<CycleMode, string> = {
  Monthly: "monthly",
  Quarterly: "quarterly",
  Six_Month: "half-yearly",
  Yearly: "yearly",
};

/** "2 Jul 2026" from a YYYY-MM-DD string. */
function fmtDayMonthYear(iso: string): string {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return iso;
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** Ordinal day-of-month, e.g. 2 → "2nd", 21 → "21st". */
function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]!);
}

/** Day count between two YYYY-MM-DD dates (start → end). */
function daysBetween(startIso: string, endIso: string): number {
  if (!startIso || !endIso) return 0;
  const a = Date.parse(`${startIso}T00:00:00Z`);
  const b = Date.parse(`${endIso}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.max(0, Math.round((b - a) / 86_400_000));
}

interface DealLine {
  key: number;
  planId: number | null;
  qty: number;
  // Recurring = open-ended, bills each cycle (quote = ONE cycle). One_Time =
  // fixed start→end window, billed in full (day-prorated).
  billingMode: BillingMode;
  // Per space type — nothing is collective.
  startDate: string;
  // Window end — used only for One_Time lines (defines the billed-in-full span).
  endDate: string;
  // Committed term used for the quote: recurring = CYCLE_MONTHS[cycleMode];
  // one-time = whole span (the day-prorated run is computed from start→end).
  durationMonths: number;
  // Day of month billing anchors to (recurring). Defaults to the start day.
  billingDay: number;
  // Phase 58.1 (ANCHOR-03): has the operator explicitly set this line's
  // anchor? Untouched lines keep auto-deriving billingDay from startDate
  // (today's byte-identical behaviour); touched lines are preserved across
  // start-date edits.
  billingDayTouched: boolean;
  // Billing cadence chosen by the operator. null = follow the period
  // (deriveCycleMode); a 3-month term can be billed Monthly or Quarterly.
  cycleMode: CycleMode | null;
  resourceIds: number[];
  // Operator override for this space type's discount % (used in "per space type"
  // discount mode). null = fall back to the plan's matrix-tier discount.
  lineDiscountPct: number | null;
  // Per space type security deposit. depositApplicable=false waives it;
  // depositAmount null = use the derived (matrix/plan) amount.
  depositApplicable: boolean;
  depositAmount: number | null;
  // 0132 — Sunday access add-on. Offered only for Private_Cabin / Workstation
  // plans that have a catalog rate; priced per unit per month x qty, taxed like
  // rent, and never touched by any discount.
  sundayAccess: boolean;
}

/** Period unit for matrix matching. */
type Unit = "Month" | "Day";

interface ResolvedLine {
  /** Effective discount % (matrix tier, else plan baseline). */
  discount: number;
  /** Refundable deposit for the whole line (matrix tier flat amount, else baseline × qty). */
  depositTotal: number;
  /** Net per-seat monthly after discount (unit price/seat/period). */
  perSeatMonthly: number;
  /** Whether a matrix tier matched (period × seats). */
  matched: boolean;
}

export default function AddPlanPage() {
  const [, params] = useRoute<{ id: string }>("/accounts/:id/add-plan");
  const [, navigate] = useLocation();
  const search = useSearch();
  const accountId = Number(params?.id);
  const backHref = `/accounts/${accountId}`;

  const { toast } = useToast();
  const qc = useQueryClient();
  const createBatch = useCreateAccountMembershipBatch();

  const { data: account, isLoading: accountLoading } = useGetAccount(accountId, {
    query: {
      enabled: Number.isFinite(accountId),
      queryKey: getGetAccountQueryKey(accountId),
    },
  });
  const { data: plans = [] } = useListInventoryPlans();
  // 0132 — Sunday access catalog rates, keyed by space type. A plan whose
  // resource type has no ACTIVE row is simply not offered the add-on, so the
  // sellable set is defined once (migration 0132's CHECK + is_active) rather
  // than hardcoded a fifth time in the UI.
  const { data: sundayRates = [] } = useListSundayAccessRates();
  const sundayRateFor = useMemo(() => {
    // Keyed as a plain string: inventory_plans.resourceType is a free text column,
    // while the generated rate type is a narrow enum.
    const byType = new Map<string, number>(
      sundayRates
        .filter((r) => r.isActive)
        .map((r) => [String(r.resourceType), Number(r.monthlyFee)]),
    );
    return (resourceType: string | null | undefined) =>
      resourceType ? byType.get(resourceType) ?? 0 : 0;
  }, [sundayRates]);
  const { data: contacts = [] } = useListAccountContacts(accountId);
  const { data: spaces = [] } = useListSpaces();
  const { data: spaceResources = [] } = useListSpaceResources(undefined, {
    query: { queryKey: getListSpaceResourcesQueryKey(undefined) },
  });
  // Phase 58.1 (ANCHOR-02) — the account's existing memberships, read
  // client-side to compute a one-click "Align to the Nth" suggestion.
  const { data: accountMemberships = [] } = useListAccountMemberships(accountId);
  const accountAnchor = useMemo(
    () => commonAccountAnchor(accountMemberships as Membership[]),
    [accountMemberships],
  );

  const keyCounter = useRef(1);
  const [contactId, setContactId] = useState<number | null>(null);
  // Lines are added by selecting space-type tiles (one line per space type).
  const [lines, setLines] = useState<DealLine[]>([]);
  // Payment mode is no longer chosen on the deal — it's recorded per payment in
  // Transactions → Plan (Record Payment / receipt). The batch enum is
  // non-nullable, so we store the default; pricing is mode-independent (all
  // modes are 0% discount), so this fixed value doesn't affect the total.
  const paymentMode: PaymentMode = "Cash";
  // GST always applies at each plan's own rate. A whole-quote discount can be
  // applied at the summary, on top of any per-space-type Discount %.
  const [additionalDiscountPct, setAdditionalDiscountPct] = useState<number>(0);
  const gstApplicable = true;

  const activePlans = useMemo(
    () => plans.filter((p) => p.status === "Active" && isMembershipPlan(p)),
    [plans],
  );
  const planById = useMemo(
    () => new Map(activePlans.map((p) => [p.id, p])),
    [activePlans],
  );

  // Period-Pricing matrix rows for every active plan — drives per-line discount
  // AND deposit (seats=qty × period). One query per plan, cached.
  const periodQueries = useQueries({
    queries: activePlans.map((p) =>
      getListInventoryPlanPeriodsQueryOptions(p.id, undefined, {
        query: { queryKey: getListInventoryPlanPeriodsQueryKey(p.id) },
      }),
    ),
  });
  const periodsReady = periodQueries.map((q) => q.dataUpdatedAt).join(",");
  const periodsByPlan = useMemo(() => {
    const map = new Map<number, InventoryPlanPeriod[]>();
    activePlans.forEach((p, i) => {
      map.set(p.id, (periodQueries[i]?.data as InventoryPlanPeriod[] | undefined) ?? []);
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activePlans, periodsReady]);

  /** Resolve a line against the matrix: tier (period×seats) → discount + deposit, else baseline. */
  function resolveLine(plan: InventoryPlan, qty: number, durationMonths: number): ResolvedLine {
    const recurring = plan.cadence === "Recurring";
    const periodValue = recurring
      ? Math.max(1, durationMonths || 1)
      : plan.validityDays ?? 1;
    const periodUnit: Unit = recurring ? "Month" : "Day";
    const unitTiers = (periodsByPlan.get(plan.id) ?? []).filter(
      (p) => (p.status ?? "Active") === "Active" && p.periodUnit === periodUnit,
    );
    // Discount: BOTH period and seats are MINIMUM thresholds / slabs
    // (2026-07-02). Pick the highest tier whose periodValue <= the term AND
    // whose seats <= qty — so a 7- or 8-month term falls into the 6-month slab
    // (until a higher slab like 12mo), and qty 2-4 uses the seats-1 tier, etc.
    // Period is ordered first, then seats.
    const slabTier = unitTiers
      .filter((p) => Number(p.periodValue) <= periodValue && Number(p.seats ?? 1) <= qty)
      .sort(
        (a, b) =>
          Number(b.periodValue) - Number(a.periodValue) ||
          Number(b.seats ?? 1) - Number(a.seats ?? 1),
      )[0];
    const discount = slabTier
      ? Number(slabTier.discountPct ?? 0)
      : plan.discountPct != null
        ? Number(plan.discountPct)
        : 0;
    // Deposit follows the SAME period/seats slab as the discount (2026-07-02):
    // if the term falls into a tier, that tier's deposit applies — and a tier
    // with the deposit toggled OFF (null) means NO deposit (e.g. quarter+). Only
    // when NO tier is at/below the term do we fall back to the plan baseline.
    const depositTotal = slabTier
      ? Number(slabTier.securityDepositAmount ?? 0)
      : plan.securityDepositRequired
        ? (Number(plan.securityDepositAmount ?? 0) > 0
            ? Number(plan.securityDepositAmount)
            : Number(plan.monthlyPrice)) * qty
        : 0;
    const perSeatMonthly = Number(plan.monthlyPrice) * (1 - discount / 100);
    return { discount, depositTotal, perSeatMonthly, matched: !!slabTier };
  }

  // Phase 54 (SPACE-UX-UNIT-KIND-10): seat picker eligibility is declaratively
  // driven by unitKind='assignable' (server-derived from resourceType, never
  // hardcoded here). Amenity rows (Pantry, Cafeteria, Store_Room, etc.) and
  // bookable rows (Meeting_Room, Conference_Room) never enter this pool — no
  // resourceType whitelist to maintain when new assignable types are added.
  const activeResources = useMemo(
    () =>
      (spaceResources as SpaceResource[]).filter(
        (r) => r.status === "Active" && r.unitKind === "assignable",
      ),
    [spaceResources],
  );
  const resourcesByType = useMemo<Record<string, SpaceResource[]>>(() => {
    const map: Record<string, SpaceResource[]> = {};
    for (const r of activeResources) (map[r.resourceType] ??= []).push(r);
    return map;
  }, [activeResources]);
  const resourceById = useMemo(
    () => new Map(activeResources.map((r) => [r.id, r])),
    [activeResources],
  );

  const issuerStateCode = useMemo(() => {
    const s = spaces.find(
      (sp) => sp.id === (account?.spaceId ?? -1),
    ) as unknown as { stateCode?: string | null } | undefined;
    return s?.stateCode ?? "07";
  }, [spaces, account]);
  const memberStateCode = account?.placeOfSupply ?? null;

  // Phase 38 (SC "Renew plan") — a deep-linked ?contactId= (e.g. from the
  // member profile's "Renew plan" action) preselects THIS member instead of
  // the account's primary contact. Runs once on mount, independent of the
  // contacts list load, so it always wins the race against the primary-
  // default effect below (that effect's `contactId != null` guard already
  // skips once this has fired).
  useEffect(() => {
    const fromQuery = new URLSearchParams(search).get("contactId");
    if (!fromQuery) return;
    const n = Number(fromQuery);
    if (Number.isFinite(n)) setContactId(n);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (contactId != null || contacts.length === 0) return;
    const primary =
      contacts.find((c) => c.roles?.includes("primary")) ?? contacts[0];
    if (primary) setContactId(primary.id);
  }, [contacts, contactId]);

  // ── Live pricing ──────────────────────────────────────────────────────────
  const validLines = useMemo(
    () => lines.filter((l) => l.planId != null && planById.has(l.planId)),
    [lines, planById],
  );

  // Resolve each valid line against the matrix (discount + deposit + per-seat).
  const resolvedByKey = useMemo(() => {
    const map = new Map<number, ResolvedLine>();
    for (const l of validLines) {
      const plan = planById.get(l.planId!)!;
      map.set(l.key, resolveLine(plan, Math.max(1, l.qty || 1), l.durationMonths));
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validLines, planById, periodsByPlan]);

  const pricing = useMemo(() => {
    if (validLines.length === 0) return null;
    function buildQuoteLine(l: DealLine): QuoteLineInput {
      const plan = planById.get(l.planId!)!;
      const r = resolvedByKey.get(l.key)!;
      // Per space type: waive → 0, else the operator's override or derived amount.
      const effectiveDeposit = l.depositApplicable
        ? (l.depositAmount ?? r.depositTotal)
        : 0;
      return {
        planId: plan.id,
        description: plan.planName.replace(/_/g, " "),
        unitPrice: Number(plan.monthlyPrice),
        qty: Math.max(1, l.qty || 1),
        durationMonths: Math.max(1, l.durationMonths || 1),
        // Per-line Discount field (detail row) overrides the matrix-tier discount.
        lineDiscountPct: l.lineDiscountPct ?? r.discount,
        taxRatePct: plan.taxRatePct != null ? Number(plan.taxRatePct) : null,
        recurring: plan.cadence === "Recurring",
        securityDepositLineTotal: effectiveDeposit,
        sundayAccessMonthly: l.sundayAccess ? sundayRateFor(plan.resourceType) : 0,
      };
    }

    // Everything is per space type — each line prices with its OWN start date,
    // derived term (durationMonths from start→end) and billing anchor, then we
    // sum for the deal preview. No collective start date / period.
    const perLine = validLines.map((l) =>
      computeQuotePricing({
        lines: [buildQuoteLine(l)],
        startDate: l.startDate,
        // One-time: pass the window end → the engine bills the day-prorated
        // start→end run in full. Recurring: null → quote = one billing cycle
        // (durationMonths = CYCLE_MONTHS), open-ended.
        endDate: l.billingMode === "One_Time" ? l.endDate : null,
        billingCycleDay: l.billingDay,
        additionalDiscountPct: additionalDiscountPct,
        paymentMode,
        gstApplicable,
        issuerStateCode,
        memberStateCode,
      }),
    );
    const sum = (pick: (p: typeof perLine[number]) => number) =>
      Math.round(perLine.reduce((s, p) => s + pick(p), 0) * 100) / 100;
    return {
      lines: perLine.flatMap((p) => p.lines),
      subtotal: sum((p) => p.subtotal),
      grossSubtotal: sum((p) => p.grossSubtotal),
      planDiscountTotal: sum((p) => p.planDiscountTotal),
      planDiscountPct: 0, // weighted average not meaningful across lines
      additionalDiscountPct: additionalDiscountPct,
      additionalDiscountAmount: sum((p) => p.additionalDiscountAmount),
      paymentModeDiscountPct: 0,
      paymentModeDiscountAmount: sum((p) => p.paymentModeDiscountAmount),
      totalDiscount: sum((p) => p.totalDiscount),
      taxableAfterDiscounts: sum((p) => p.taxableAfterDiscounts),
      sundayAccessTotal: sum((p) => p.sundayAccessTotal),
      cgstTotal: sum((p) => p.cgstTotal),
      sgstTotal: sum((p) => p.sgstTotal),
      igstTotal: sum((p) => p.igstTotal),
      securityDepositTotal: sum((p) => p.securityDepositTotal),
      monthlyRecurringTotal: sum((p) => p.monthlyRecurringTotal),
      grandTotal: sum((p) => p.grandTotal),
      roundOff: sum((p) => p.roundOff),
      roundedGrandTotal: sum((p) => p.roundedGrandTotal),
    };
  }, [
    validLines,
    planById,
    resolvedByKey,
    additionalDiscountPct,
    paymentMode,
    gstApplicable,
    issuerStateCode,
    memberStateCode,
  ]);

  const pricingByKey = useMemo(() => {
    const map = new Map<number, number>();
    validLines.forEach((l, j) => map.set(l.key, j));
    return map;
  }, [validLines]);

  /** Max bookable quantity for a plan = count of Active resources of its type. */
  function availLimit(plan?: InventoryPlan): number | null {
    if (!plan?.resourceType) return null;
    return resourcesByType[plan.resourceType]?.length ?? 0;
  }

  /** Toggle a space type in/out of the deal (one line per space type). */
  function togglePlan(planId: number) {
    setLines((prev) => {
      if (prev.some((l) => l.planId === planId)) {
        return prev.filter((l) => l.planId !== planId);
      }
      const today = new Date().toISOString().slice(0, 10);
      return [
        ...prev,
        {
          key: keyCounter.current++,
          planId,
          qty: 1,
          billingMode: "Recurring",
          startDate: today,
          endDate: addMonths(today, 1),
          durationMonths: 1, // one Monthly cycle (open-ended recurring quote)
          billingDay: billingDayOf(today),
          billingDayTouched: false,
          cycleMode: "Monthly",
          resourceIds: [],
          lineDiscountPct: null,
          depositApplicable: true,
          depositAmount: null,
          sundayAccess: false,
        },
      ];
    });
  }

  function removeLine(key: number) {
    setLines((prev) => prev.filter((l) => l.key !== key));
  }

  function patchLine(key: number, patch: Partial<DealLine>) {
    setLines((prev) => prev.map((l) => (l.key === key ? { ...l, ...patch } : l)));
  }

  // Recurring term = one billing cycle; one-time term = whole start→end span.
  function termMonths(l: DealLine): number {
    return l.billingMode === "One_Time"
      ? monthsBetween(l.startDate, l.endDate)
      : CYCLE_MONTHS[l.cycleMode ?? "Monthly"];
  }
  function setLineBillingMode(key: number, mode: BillingMode) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        if (mode === "One_Time") {
          // Ensure a valid window end (>= a day after start) for the billed-in-full run.
          const endDate = l.endDate && l.endDate > l.startDate ? l.endDate : addMonths(l.startDate, 1);
          return { ...l, billingMode: mode, endDate, durationMonths: monthsBetween(l.startDate, endDate) };
        }
        return { ...l, billingMode: mode, durationMonths: CYCLE_MONTHS[l.cycleMode ?? "Monthly"] };
      }),
    );
  }
  function setLineCycle(key: number, cycleMode: CycleMode) {
    setLines((prev) =>
      prev.map((l) =>
        l.key === key ? { ...l, cycleMode, durationMonths: CYCLE_MONTHS[cycleMode] } : l,
      ),
    );
  }
  function setLineStart(key: number, startDate: string) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key || !startDate) return l;
        const endDate =
          l.endDate && l.endDate > startDate ? l.endDate : addMonths(startDate, 1);
        const next = {
          ...l,
          startDate,
          endDate,
          billingDay: nextBillingDayOnStartChange({
            startDate,
            currentBillingDay: l.billingDay,
            touched: l.billingDayTouched,
          }),
        };
        return { ...next, durationMonths: termMonths(next) };
      }),
    );
  }
  function setLineEnd(key: number, endDate: string) {
    setLines((prev) =>
      prev.map((l) =>
        l.key === key && endDate
          ? { ...l, endDate, durationMonths: monthsBetween(l.startDate, endDate) }
          : l,
      ),
    );
  }

  function setLineQty(key: number, qty: number) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        // Don't clamp to availLimit — operators sometimes book more
        // seats than currently visible (new resources coming online,
        // intent-to-acquire). The Assign-resource picker still only
        // shows real available resources, so over-booked qty just
        // surfaces as "x/N selected" until more seats exist. The
        // lower bound stays — qty ≥ 1 always.
        const cleaned = Math.max(1, qty);
        return { ...l, qty: cleaned, resourceIds: l.resourceIds.slice(0, cleaned) };
      }),
    );
  }

  function toggleResource(key: number, id: number, checked: boolean) {
    setLines((prev) =>
      prev.map((l) => {
        if (l.key !== key) return l;
        if (checked) {
          if (l.resourceIds.includes(id) || l.resourceIds.length >= l.qty) return l;
          return { ...l, resourceIds: [...l.resourceIds, id] };
        }
        return { ...l, resourceIds: l.resourceIds.filter((x) => x !== id) };
      }),
    );
  }

  async function onSubmit() {
    if (!contactId) {
      toast({
        title: "This account has no contact to bill.",
        description: "Add a member under the account's Members tab first.",
        variant: "destructive",
      });
      return;
    }
    if (validLines.length === 0) {
      toast({ title: "Add at least one plan line.", variant: "destructive" });
      return;
    }
    try {
      const firstLine = validLines[0];

      await createBatch.mutateAsync({
        id: accountId,
        data: {
          contactId,
          // Body-level fields are a fallback for the API contract only — every
          // line sends its own dates, billing, term and deposit below.
          startDate: firstLine?.startDate ?? new Date().toISOString().slice(0, 10),
          endDate: null,
          billingCycleDay: firstLine?.billingDay ?? 1,
          paymentMode,
          additionalDiscountPct: additionalDiscountPct,
          gstApplicable,
          securityDepositApplicable: true,
          securityDepositHeld: null,
          createProforma: true,
          lines: validLines.map((l) => {
            const oneTime = l.billingMode === "One_Time";
            const r = resolvedByKey.get(l.key);
            const seatRefs = l.resourceIds
              .map((id) => resourceById.get(id)?.label)
              .filter(Boolean);
            return {
              planId: l.planId!,
              quantity: Math.max(1, l.qty || 1),
              durationMonths: Math.max(1, l.durationMonths || 1),
              // One-time → One_Time cycle (billed in full over start→end); else
              // the operator-chosen recurring cadence.
              cycleMode: oneTime ? "One_Time" : l.cycleMode ?? "Monthly",
              lineDiscountPct: l.lineDiscountPct ?? r?.discount ?? 0,
              sundayAccess: l.sundayAccess,
              seatOrCabinRef: seatRefs.length ? seatRefs.join(", ") : null,
              startDate: l.startDate,
              // One-time carries its window end; recurring is open-ended.
              endDate: oneTime ? l.endDate : null,
              // No monthly billing day for a one-time line.
              billingCycleDay: oneTime ? null : l.billingDay,
              // Per space type deposit — waive or override persists per line.
              securityDepositApplicable: l.depositApplicable,
              securityDepositHeld: l.depositApplicable
                ? l.depositAmount ?? r?.depositTotal ?? 0
                : null,
            };
          }),
        },
      });
      toast({
        title: "Plan(s) added.",
        description: "Memberships created and a proforma estimate saved.",
      });
      qc.invalidateQueries({ queryKey: getListAccountMembershipsQueryKey(accountId) });
      qc.invalidateQueries({ queryKey: getListAccountProformasQueryKey(accountId) });
      qc.invalidateQueries({ queryKey: ["accounts"] });
      qc.invalidateQueries({ queryKey: getListSpaceResourcesQueryKey(undefined) });
      navigate(backHref);
    } catch (err) {
      const detail = (err as Error)?.message ?? "Please try again.";
      toast({
        title: "Couldn't add the plan(s).",
        description: detail,
        variant: "destructive",
      });
    }
  }

  const intraState = pricing != null && pricing.igstTotal === 0;

  // "Ongoing after this invoice" — recurring lines grouped by cadence, GST-inclusive.
  const ongoingRows: Array<[string, number]> = (() => {
    if (!pricing) return [];
    const m = new Map<string, number>();
    for (const l of validLines) {
      if (l.billingMode === "One_Time") continue;
      const idx = pricingByKey.get(l.key);
      const lr = idx != null ? pricing.lines[idx] : undefined;
      if (!lr) continue;
      const cyc = l.cycleMode ?? "Monthly";
      const label = `${CYCLE_LABEL[cyc][0]!.toUpperCase()}${CYCLE_LABEL[cyc].slice(1)}`;
      m.set(
        label,
        (m.get(label) ?? 0) +
          lr.lineMonthly * (1 - additionalDiscountPct / 100) * (1 + lr.gstRate / 100),
      );
    }
    return [...m.entries()];
  })();

  if (!Number.isFinite(accountId)) {
    return (
      <div className="mx-auto w-full max-w-4xl p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">Invalid account id.</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-4xl space-y-4 p-4 sm:p-6">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => navigate(backHref)}
      >
        <ArrowLeft className="mr-1 h-4 w-4" />
        Back
      </Button>

      <div className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">Add Plan</h1>
        <p className="text-sm text-muted-foreground">
          {accountLoading ? (
            <Skeleton className="inline-block h-4 w-48 align-middle" />
          ) : (
            <>
              Build a deal for{" "}
              <span className="font-medium">{account?.legalName ?? "this account"}</span>{" "}
              — add space types, set duration, and review the total.
            </>
          )}
        </p>
      </div>

      {/* Dates, billing day, term and status are now per space type — set on
          each line below. Nothing is collective at the deal level. */}

      {/* Plans — pick space types as tiles */}
      <div className="space-y-3">
        <Label>Plans</Label>

        {activePlans.length === 0 && (
          <p className="rounded-md border bg-muted/30 px-3 py-6 text-center text-sm text-muted-foreground">
            No Active plans configured. Create one under Inventory → Plans.
          </p>
        )}

        {activePlans.length > 0 && (
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {activePlans.map((p) => {
              const selected = lines.some((l) => l.planId === p.id);
              const recurringTile = p.cadence === "Recurring";
              const limit = p.resourceType
                ? resourcesByType[p.resourceType]?.length ?? 0
                : null;
              const periodLabel = recurringTile
                ? BILLING_CYCLE_LABELS[p.billingCycle] ?? "Monthly"
                : p.validityDays
                  ? `${p.validityDays}-day pass`
                  : "Per booking";
              const depositReq = !!p.securityDepositRequired;
              const depositAmt =
                Number(p.securityDepositAmount ?? 0) > 0
                  ? Number(p.securityDepositAmount)
                  : Number(p.monthlyPrice);
              const tierMax = (periodsByPlan.get(p.id) ?? []).reduce(
                (m, pr) =>
                  (pr.status ?? "Active") === "Active"
                    ? Math.max(m, Number(pr.discountPct ?? 0))
                    : m,
                0,
              );
              const maxDisc = Math.max(
                tierMax,
                p.discountPct != null ? Number(p.discountPct) : 0,
              );
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => togglePlan(p.id)}
                  className={cn(
                    "relative flex flex-col gap-2 rounded-md border p-3 text-left transition-colors",
                    selected
                      ? "border-primary bg-primary/5 ring-1 ring-primary"
                      : "hover:bg-muted/50",
                  )}
                >
                  {selected && (
                    <Check className="absolute right-2 top-2 h-4 w-4 text-primary" />
                  )}
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        "flex h-8 w-8 shrink-0 items-center justify-center rounded-md",
                        selected
                          ? "bg-primary/10 text-primary"
                          : "bg-muted text-muted-foreground",
                      )}
                    >
                      <PlanIcon resourceType={p.resourceType} className="h-4 w-4" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium">
                        {p.planName.replace(/_/g, " ")}
                      </span>
                      <span className="block text-xs text-muted-foreground">
                        {formatINR(Number(p.monthlyPrice))}
                        {recurringTile ? "/mo" : ""}
                      </span>
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {limit != null && (
                      <Badge variant="secondary" className="text-[10px]">
                        {limit} available
                      </Badge>
                    )}
                    <Badge variant="outline" className="text-[10px]">
                      {periodLabel}
                    </Badge>
                    <Badge variant="outline" className="text-[10px]">
                      {depositReq ? `Deposit ${formatINR(depositAmt)}` : "No deposit"}
                    </Badge>
                    {maxDisc > 0 && (
                      <Badge variant="outline" className="text-[10px]">
                        up to {maxDisc}% off
                      </Badge>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        )}

        {lines.length > 0 && (
          <div className="space-y-3">
            {lines.map((line) => {
              const plan = line.planId != null ? planById.get(line.planId) : undefined;
              const oneTime = line.billingMode === "One_Time";
              const limit = availLimit(plan);
              const pIdx = pricingByKey.get(line.key);
              const lineResult = pIdx != null && pricing ? pricing.lines[pIdx] : undefined;
              const res = resolvedByKey.get(line.key);
              // resourcesByType is already scoped to unitKind='assignable' rows
              // (see the activeResources filter above), so a plan whose
              // resourceType never appears as a key there (e.g. a bookable
              // room type) simply yields an empty pool — no separate
              // seat-holdable whitelist needed.
              const allOfType = plan?.resourceType
                ? resourcesByType[plan.resourceType] ?? []
                : [];
              const takenElsewhere = new Set(
                lines.filter((o) => o.key !== line.key).flatMap((o) => o.resourceIds),
              );
              const pickable = allOfType.filter((r) => !takenElsewhere.has(r.id));
              const cyc = line.cycleMode ?? "Monthly";
              const lineDeposit = line.depositApplicable
                ? line.depositAmount ?? res?.depositTotal ?? 0
                : 0;
              const bigUnit = oneTime
                ? "one-time"
                : { Monthly: "/ month", Quarterly: "/ quarter", Six_Month: "/ 6 mo", Yearly: "/ year" }[cyc];
              const stubDays = stubDaysOf(line.startDate, line.billingDay);
              const summary = oneTime
                ? `${fmtDayMonthYear(line.startDate)} → ${fmtDayMonthYear(line.endDate)} · ${daysBetween(line.startDate, line.endDate)} days · billed in full on the first invoice${lineDeposit > 0 ? ` · deposit ${formatINR(lineDeposit)}` : ""}`
                : `Starts ${fmtDayMonthYear(line.startDate)} · bills ${CYCLE_LABEL[cyc]} on the ${ordinal(line.billingDay)} · ${stubDays === 0 ? "first full cycle bills now" : `first ${stubDays} days prorated`}${lineDeposit > 0 ? ` · deposit ${formatINR(lineDeposit)}` : ""}`;
              return (
                <div key={line.key} className="overflow-hidden rounded-md border">
                  {/* Headline — plan name (always fully visible) + Recurring/One-time
                      toggle on the right (default Recurring). Remove a space type by
                      de-selecting its tile above. */}
                  <div className="flex items-center justify-between gap-2 border-b bg-muted/20 px-4 py-2.5">
                    <span className="flex items-center gap-2">
                      <PlanIcon
                        resourceType={plan?.resourceType}
                        className="h-4 w-4 shrink-0 text-muted-foreground"
                      />
                      <span className="text-sm font-semibold">
                        {plan?.planName.replace(/_/g, " ")}
                      </span>
                    </span>
                    <div className="inline-flex shrink-0 overflow-hidden rounded-md border">
                      {(["Recurring", "One_Time"] as const).map((m) => (
                        <button
                          key={m}
                          type="button"
                          className={cn(
                            "px-3 py-1.5 text-xs",
                            (m === "One_Time") === oneTime
                              ? "bg-primary text-primary-foreground"
                              : "bg-background text-muted-foreground hover:bg-muted",
                          )}
                          onClick={() => setLineBillingMode(line.key, m)}
                        >
                          {m === "One_Time" ? "One-time" : "Recurring"}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Controls — start date · billing cycle (or end date) · qty ·
                      price per seat · price incl GST. */}
                  <div className="flex flex-wrap items-end gap-x-5 gap-y-3 px-4 pt-4">
                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Start date</p>
                      <Input
                        type="date"
                        value={line.startDate}
                        onChange={(e) => setLineStart(line.key, e.target.value)}
                        className="h-9 w-40"
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                        {oneTime ? "End date" : "Billing cycle"}
                      </p>
                      {oneTime ? (
                        <Input
                          type="date"
                          value={line.endDate}
                          min={line.startDate || undefined}
                          onChange={(e) => setLineEnd(line.key, e.target.value)}
                          className="h-9 w-40"
                        />
                      ) : (
                        <Select value={cyc} onValueChange={(v) => setLineCycle(line.key, v as CycleMode)}>
                          <SelectTrigger className="h-9 w-40">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Monthly">Monthly</SelectItem>
                            <SelectItem value="Quarterly">Quarterly</SelectItem>
                            <SelectItem value="Six_Month">Half-yearly</SelectItem>
                            <SelectItem value="Yearly">Yearly</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>

                    {!oneTime && (
                      <div>
                        <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Bills on day</p>
                        <Input
                          type="number"
                          min={1}
                          max={31}
                          className="h-9 w-20"
                          value={line.billingDay}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            patchLine(line.key, {
                              billingDay: Math.min(31, Math.max(1, Number(e.target.value) || 1)),
                              billingDayTouched: true,
                            })
                          }
                        />
                      </div>
                    )}

                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Qty</p>
                      <div className="flex items-center">
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-r-none"
                          onClick={() => setLineQty(line.key, Math.max(1, line.qty - 1))}
                        >
                          −
                        </Button>
                        <Input
                          type="number"
                          min={1}
                          className="h-9 w-12 rounded-none border-x-0 px-0 text-center"
                          value={line.qty}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            setLineQty(line.key, Math.max(1, Number(e.target.value) || 1))
                          }
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="h-9 w-9 rounded-l-none"
                          onClick={() => setLineQty(line.key, line.qty + 1)}
                        >
                          +
                        </Button>
                      </div>
                      {limit != null && line.qty > limit && (
                        <span className="mt-0.5 block text-[10px] text-amber-700">
                          Only {limit} available now.
                        </span>
                      )}
                    </div>

                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Security deposit</p>
                      <Input
                        type="number"
                        min={0}
                        value={line.depositAmount ?? res?.depositTotal ?? 0}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const v = e.target.value;
                          patchLine(line.key, {
                            depositAmount: v === "" ? null : Math.max(0, Number(v)),
                          });
                        }}
                        className="h-9 w-32"
                      />
                    </div>

                    <div>
                      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Discount %</p>
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step="0.01"
                        value={line.lineDiscountPct ?? res?.discount ?? 0}
                        onFocus={(e) => e.target.select()}
                        onChange={(e) => {
                          const v = e.target.value;
                          patchLine(line.key, {
                            lineDiscountPct:
                              v === "" ? null : Math.max(0, Math.min(100, Number(v))),
                          });
                        }}
                        className="h-9 w-24"
                      />
                    </div>
                    {/* 0132 — Sunday access. Shown only when this plan's space
                        type has an active catalog rate, so the operator never
                        sees a toggle that cannot be priced. Flat rate x qty,
                        taxed like rent, untouched by either discount. */}
                    {(() => {
                      const plan = line.planId != null ? planById.get(line.planId) : null;
                      const rate = sundayRateFor(plan?.resourceType);
                      if (!plan || rate <= 0 || plan.cadence !== "Recurring") return null;
                      const qty = Math.max(1, line.qty || 1);
                      return (
                        <div>
                          <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                            Sunday access
                          </p>
                          <label className="flex h-9 items-center gap-2 text-sm">
                            <input
                              type="checkbox"
                              className="h-4 w-4 accent-primary"
                              checked={line.sundayAccess}
                              onChange={(e) =>
                                patchLine(line.key, { sundayAccess: e.target.checked })
                              }
                            />
                            <span className="whitespace-nowrap">
                              +{formatINR(rate * qty)}/mo
                            </span>
                          </label>
                        </div>
                      );
                    })()}
                  </div>
                  {plan && res?.matched && res.discount > 0 && (
                    <p className="px-4 pt-1 text-[10px] text-muted-foreground">
                      Matrix tier — {res.discount}% off applied automatically.
                    </p>
                  )}

                  {/* Summary — schedule narrative (informative, read-only). */}
                  <div className="mt-3 border-y bg-muted/40 px-4 py-2 text-xs text-muted-foreground">
                    {summary}
                  </div>

                  {/* Phase 58.1 (ANCHOR-02) — one-click align to the account's
                      single common recurring billing anchor, when this line
                      differs from it. */}
                  {!oneTime && accountAnchor != null && accountAnchor !== line.billingDay && (
                    <p className="border-b bg-muted/20 px-4 py-1.5 text-xs text-muted-foreground">
                      This account&apos;s other plans bill on the {ordinal(accountAnchor)}.{" "}
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="h-auto p-0 text-xs"
                        onClick={() =>
                          patchLine(line.key, {
                            billingDay: accountAnchor,
                            billingDayTouched: true,
                          })
                        }
                      >
                        Align to the {ordinal(accountAnchor)}
                      </Button>
                    </p>
                  )}

                  {/* Footer — informative price line: per seat + inclusive of GST,
                      both the same text size / style. */}
                  <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-t bg-muted/20 px-4 py-2.5 text-sm text-muted-foreground">
                    <span>
                      Price / seat{" "}
                      <span className="font-medium text-foreground tabular-nums">
                        {formatINR(res?.perSeatMonthly ?? 0)}
                      </span>{" "}
                      / mo
                    </span>
                    <span>
                      Incl. GST{" "}
                      <span className="font-medium text-foreground tabular-nums">
                        {formatINR(lineResult?.lineTotal ?? 0)}
                      </span>{" "}
                      {bigUnit}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Deal-level discount + GST controls removed — discount is now per space
          type (each card's Discount %), GST always applies at the plan's rate,
          and Security Deposit is per space type. */}

      {/* Summary — organized BY ITEM (each item's full money story), then a
          payable-now totals block, then the forward-looking ongoing box. */}
      <div className="space-y-4 sm:ml-auto sm:max-w-lg">
        {/* By item */}
        <div className="overflow-hidden rounded-md border">
          <p className="border-b bg-muted/20 px-4 py-2 text-[10px] uppercase tracking-wide text-muted-foreground">
            By item
          </p>
          {validLines.length === 0 || !pricing ? (
            <p className="px-4 py-6 text-center text-sm text-muted-foreground">
              Add a space type to see the estimate.
            </p>
          ) : (
            <div className="divide-y">
              {validLines.map((l) => {
                const idx = pricingByKey.get(l.key);
                const lr = idx != null ? pricing.lines[idx] : undefined;
                const plan = planById.get(l.planId!);
                const oneTime = l.billingMode === "One_Time";
                const cyc = l.cycleMode ?? "Monthly";
                const intra = (lr?.igstAmount ?? 0) === 0;
                const gst = (lr?.cgstAmount ?? 0) + (lr?.sgstAmount ?? 0) + (lr?.igstAmount ?? 0);
                const deposit = lr?.securityDeposit ?? 0;
                const dueNow = (lr?.lineTotal ?? 0) + deposit;
                const stub = oneTime ? 0 : stubDaysOf(l.startDate, l.billingDay);
                const cycNoun = { Monthly: "month", Quarterly: "quarter", Six_Month: "6 months", Yearly: "year" }[cyc];
                const firstLabel = oneTime
                  ? `Billed in full · ${daysBetween(l.startDate, l.endDate)} days`
                  : stub > 0
                    ? `Prorated ${stub} days`
                    : `First ${cycNoun}`;
                // Recurring rate reflects the Additional Discount on the monthly/seat
                // (matches the server's frozen per-unit = monthly × (1−disc) × (1−addl)).
                const recurringInclGst = oneTime
                  ? 0
                  : (lr?.lineMonthly ?? 0) * (1 - additionalDiscountPct / 100) * (1 + (lr?.gstRate ?? 0) / 100);
                const fromDate = oneTime ? "" : addMonths(l.startDate, CYCLE_MONTHS[cyc]);
                const badge = oneTime
                  ? "One-time"
                  : `${CYCLE_LABEL[cyc][0]!.toUpperCase()}${CYCLE_LABEL[cyc].slice(1)} recurring`;
                return (
                  <div key={l.key} className="px-4 py-3">
                    <div className="mb-1.5 flex items-center justify-between gap-2">
                      <span className="text-sm font-semibold">{plan?.planName.replace(/_/g, " ")}</span>
                      <Badge variant="secondary" className="shrink-0 text-[10px]">{badge}</Badge>
                    </div>
                    <div className="space-y-0.5 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{firstLabel}</span>
                        <span className="tabular-nums">{formatINR(lr?.taxableAmount ?? 0)}</span>
                      </div>
                      {gst > 0 && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span className="pl-3">
                            {intra ? `CGST ${lr?.cgstRate}% + SGST ${lr?.sgstRate}%` : `IGST ${lr?.igstRate}%`}
                          </span>
                          <span className="tabular-nums">{formatINR(gst)}</span>
                        </div>
                      )}
                      {deposit > 0 && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Deposit · refundable · GST-free</span>
                          <span className="tabular-nums">{formatINR(deposit)}</span>
                        </div>
                      )}
                      <div className="flex justify-between border-t pt-1 font-semibold">
                        <span>Due now</span>
                        <span className="tabular-nums">{formatINR(dueNow)}</span>
                      </div>
                      {!oneTime && (
                        <p className="pt-0.5 text-[11px] text-muted-foreground">
                          Then {formatINR(recurringInclGst)}/month incl. GST, from{" "}
                          <span className="font-medium">{fmtDayMonthYear(fromDate)}</span>
                        </p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Totals — payable now */}
        <div className="rounded-md border p-4 text-sm">
          <p className="mb-2 text-[10px] uppercase tracking-wide text-muted-foreground">Totals — payable now</p>
          {pricing && pricing.additionalDiscountAmount > 0 && (
            <div className="flex justify-between py-0.5 text-muted-foreground">
              <span>Additional discount ({pricing.additionalDiscountPct}%)</span>
              <span className="tabular-nums">−{formatINR(pricing.additionalDiscountAmount)}</span>
            </div>
          )}
          {(pricing?.sundayAccessTotal ?? 0) > 0 && (
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">Sunday access</span>
              <span className="tabular-nums">{formatINR(pricing!.sundayAccessTotal)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5">
            <span className="text-muted-foreground">Taxable value</span>
            <span className="tabular-nums">{formatINR(pricing?.taxableAfterDiscounts ?? 0)}</span>
          </div>
          {intraState ? (
            <>
              <div className="flex justify-between py-0.5">
                <span className="text-muted-foreground">CGST</span>
                <span className="tabular-nums">{formatINR(pricing?.cgstTotal ?? 0)}</span>
              </div>
              <div className="flex justify-between py-0.5">
                <span className="text-muted-foreground">SGST</span>
                <span className="tabular-nums">{formatINR(pricing?.sgstTotal ?? 0)}</span>
              </div>
            </>
          ) : (
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">IGST</span>
              <span className="tabular-nums">{formatINR(pricing?.igstTotal ?? 0)}</span>
            </div>
          )}
          <div className="flex justify-between py-0.5">
            <span className="text-muted-foreground">Invoice total</span>
            <span className="tabular-nums">
              {formatINR(
                (pricing?.taxableAfterDiscounts ?? 0) +
                  (pricing?.cgstTotal ?? 0) +
                  (pricing?.sgstTotal ?? 0) +
                  (pricing?.igstTotal ?? 0),
              )}
            </span>
          </div>
          {pricing && pricing.securityDepositTotal > 0 && (
            <div className="flex justify-between py-0.5">
              <span className="text-muted-foreground">Deposits (GST-free)</span>
              <span className="tabular-nums">{formatINR(pricing.securityDepositTotal)}</span>
            </div>
          )}
          <div className="mt-1 flex justify-between border-t pt-2 text-lg font-bold">
            <span>Payable now</span>
            <span className="tabular-nums">{formatINR(pricing?.roundedGrandTotal ?? 0)}</span>
          </div>
        </div>

        {/* Ongoing after this invoice */}
        {ongoingRows.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50/60 p-4 text-sm">
            <p className="font-semibold">Ongoing after this invoice</p>
            <p className="mb-1 text-[11px] text-muted-foreground">
              billed automatically on each billing day · incl. GST
            </p>
            {ongoingRows.map(([cadence, amt]) => (
              <div key={cadence} className="flex justify-between py-0.5">
                <span>{cadence}</span>
                <span className="tabular-nums">{formatINR(amt)}</span>
              </div>
            ))}
          </div>
        )}

        {/* Additional Discount — applies across every item on the Monthly/seat
            rate, on top of each space type's own Discount %. */}
        <div className="flex items-center justify-between gap-3 rounded-md border p-4">
          <div>
            <Label htmlFor="additional-discount" className="text-sm">Additional Discount</Label>
            <p className="text-[10px] text-muted-foreground">Applied on the Monthly / seat rate.</p>
          </div>
          <div className="flex items-center gap-1.5">
            <Input
              id="additional-discount"
              type="number"
              min={0}
              max={100}
              value={additionalDiscountPct}
              onFocus={(e) => e.target.select()}
              onChange={(e) =>
                setAdditionalDiscountPct(Math.max(0, Math.min(100, Number(e.target.value) || 0)))
              }
              className="h-9 w-20 text-right"
            />
            <span className="text-sm text-muted-foreground">%</span>
          </div>
        </div>

        <p className="text-[11px] text-muted-foreground">
          Proforma <span className="font-medium">estimate</span>, not a tax invoice. Recurring plans
          bill automatically on the billing day; prorated first stubs shown here are system estimates.
        </p>
      </div>

      <div className="flex justify-end gap-2 border-t pt-4">
        <Button variant="outline" onClick={() => navigate(backHref)} disabled={createBatch.isPending}>
          Discard
        </Button>
        <Button
          onClick={onSubmit}
          disabled={!contactId || validLines.length === 0 || createBatch.isPending}
        >
          {createBatch.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Add Plan &amp; Save Estimate
        </Button>
      </div>
    </div>
  );
}
